import { useState, useEffect, useRef, useCallback } from 'react';
import { useRelayEnvironment, type Environment } from 'react-relay';
import { graphql, fetchQuery } from 'react-relay';

import { canDisplayFavoritesNudge } from './helpers/canDisplayFavoritesNudge.ts';
import { scheduleNudgeDisplay } from './helpers/scheduleNudgeDisplay.ts';

import { HIDE_NUDGE_ITEM_FAVORITE_COUNT, FAVORITES_LOCAL_STORAGE_KEYS } from './constants.ts';

import { type useFavoritesNudgeQuery } from './__generated__/useFavoritesNudgeQuery.graphql.ts';

const query = graphql`
    query useFavoritesNudgeQuery(
        $userId: String!
        $itemId: String!
        $fetchHeartPortfolioMatches: Boolean!
    ) {
        viewer {
            heartPortfolios: portfolios(first: 1, portfolioTypes: [HEART], userIds: [$userId]) {
                edges {
                    node {
                        items(first: 1) {
                            totalResults
                        }
                    }
                }
            }
            heartPortfolioMatches: portfolioItemMatches(
                portfolioTypes: [HEART]
                userIds: [$userId]
                itemIds: [$itemId]
            ) @include(if: $fetchHeartPortfolioMatches) {
                item {
                    serviceId
                }
            }
        }
    }
`;

async function fetchFavoritesNudgeData({
    userId,
    itemId,
    environment,
}: {
    userId: string | null | undefined;
    itemId: string | null | undefined;
    environment: Environment;
}): Promise<{
    favoritesCount: number;
    isItemLiked: boolean;
}> {
    const nudgeData = { favoritesCount: 0, isItemLiked: false };

    if (!userId) {
        return nudgeData;
    }

    try {
        const data = await fetchQuery<useFavoritesNudgeQuery>(environment, query, {
            userId,
            itemId: itemId || '',
            fetchHeartPortfolioMatches: !!itemId,
        }).toPromise();

        const { heartPortfolios, heartPortfolioMatches } = data?.viewer || {};
        const userFavoritesCount = heartPortfolios?.edges?.[0]?.node?.items?.totalResults ?? 0;
        nudgeData.favoritesCount = userFavoritesCount;

        if (itemId && heartPortfolioMatches?.find(heart => heart?.item?.serviceId === itemId)) {
            nudgeData.isItemLiked = true;
        }

        return nudgeData;
    } catch {
        return nudgeData;
    }
}

const useFavoritesNudge = ({
    userId,
    itemId,
    showNudge,
}: {
    userId: string | null | undefined;
    itemId: string | null | undefined;
    showNudge: boolean;
}): { isVisible: boolean; hideNudge: () => void } => {
    const environment = useRelayEnvironment();
    const [isVisible, setIsVisible] = useState(false);
    const mountedRef = useRef(false);
    const cleanupRef = useRef<(() => void) | null>(null);

    const hideNudge = useCallback(() => {
        setIsVisible(false);
    }, []);

    const handleShowNudge = useCallback(async (): Promise<void> => {
        mountedRef.current = true;

        const { favoritesCount, isItemLiked } = await fetchFavoritesNudgeData({
            userId,
            itemId,
            environment,
        });

        if (!mountedRef.current || isItemLiked) {
            return;
        }

        if (favoritesCount >= HIDE_NUDGE_ITEM_FAVORITE_COUNT) {
            localStorage.setItem(FAVORITES_LOCAL_STORAGE_KEYS.FAVORITES_NUDGE_HIDE, 'true');
            return;
        }

        cleanupRef.current = scheduleNudgeDisplay({ mountedRef, itemId, setIsVisible });
    }, [userId, itemId, environment]);

    useEffect(() => {
        if (!showNudge || !canDisplayFavoritesNudge()) {
            return () => {
                if (cleanupRef.current) {
                    cleanupRef.current();
                }
            };
        }

        handleShowNudge();

        return () => {
            mountedRef.current = false;

            if (cleanupRef.current) {
                cleanupRef.current();
                cleanupRef.current = null;
            }
        };
    }, [showNudge, handleShowNudge]);

    return { isVisible, hideNudge };
};

export { useFavoritesNudge };
